import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Notification } from 'src/assets/types/interface/notification.interface';
import { EMPTY, Observable, Subject } from 'rxjs';
import { NotificationPage } from 'src/assets/types/interface/notification-page.interface';
import { io, Socket } from 'socket.io-client';
import { NotificationCreatedEvent } from 'src/assets/types/interface/notification-created-event.interface';
import { NotificationReadEvent } from 'src/assets/types/interface/notification-read-event.interface';
import { SidebarCountersUpdatedEvent } from 'src/assets/types/interface/sidebar-counters-updated-event.interface';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly apiUrl = environment.NOTIFICATION_API_URL;
  private readonly socketUrl = environment.NOTIFICATION_API_URL.replace(
    'http',
    'ws',
  );
  private readonly activeRouteRefreshSubject = new Subject<void>();

  readonly activeRouteRefreshRequested$ =
    this.activeRouteRefreshSubject.asObservable();

  private socket?: Socket;
  constructor(private http: HttpClient) {}

  requestActiveRouteRefresh(): void {
    this.activeRouteRefreshSubject.next();
  }

  hasAccessToken(): boolean {
    return this.getAccessToken() !== null;
  }

  connect(): void {
    const accessToken = this.getAccessToken();
    if (!accessToken || this.socket) return;

    const socket = io(this.socketUrl, {
      transports: ['websocket'],
      auth: {
        token: accessToken,
      },
    });
    this.socket = socket;

    socket.on('connect', () => {
      socket.emit('register-tenant', accessToken);
      // console.log('Socket conectado');
    });

    socket.on('disconnect', () => {
      // console.log('Socket desconectado');
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = undefined;
    }
  }

  listenToNotifications(): Observable<NotificationCreatedEvent> {
    const socket = this.socket;
    if (!socket) return EMPTY;

    return new Observable((observer) => {
      const handler = (data: NotificationCreatedEvent) => observer.next(data);
      const errorHandler = (err: any) => observer.error(err);

      socket.on('notification-created', handler);
      socket.on('error', errorHandler);

      return () => {
        socket.off('notification-created', handler);
        socket.off('error', errorHandler);
      };
    });
  }

  listenToNotificationReads(): Observable<NotificationReadEvent> {
    const socket = this.socket;
    if (!socket) return EMPTY;

    return new Observable((observer) => {
      const handler = (data: NotificationReadEvent) => observer.next(data);
      const errorHandler = (err: any) => observer.error(err);

      socket.on('notification-read', handler);
      socket.on('error', errorHandler);

      return () => {
        socket.off('notification-read', handler);
        socket.off('error', errorHandler);
      };
    });
  }

  listenToSidebarCounterUpdates(): Observable<SidebarCountersUpdatedEvent> {
    const socket = this.socket;
    if (!socket) return EMPTY;

    return new Observable((observer) => {
      const handler = (data: SidebarCountersUpdatedEvent) =>
        observer.next(data);
      const errorHandler = (err: any) => observer.error(err);

      socket.on('sidebar-counters-updated', handler);
      socket.on('error', errorHandler);

      return () => {
        socket.off('sidebar-counters-updated', handler);
        socket.off('error', errorHandler);
      };
    });
  }

  getAllNotifications() {
    const accessToken = this.getAccessToken();
    if (!accessToken) return EMPTY;

    return this.http.get<Notification[]>(`${this.apiUrl}/notification/`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  getFiveNotifications() {
    const accessToken = this.getAccessToken();
    if (!accessToken) return EMPTY;

    return this.http.get<Notification[]>(`${this.apiUrl}/notification/five`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  markedAsRead(uuid: string): Observable<NotificationReadEvent> {
    const accessToken = this.getAccessToken();
    if (!accessToken) return EMPTY;

    return this.http.put<NotificationReadEvent>(
      `${this.apiUrl}/notification/${uuid}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );
  }

  getNotificationsWithPagination(
    take: number,
    page: number,
    filter: string,
  ): Observable<NotificationPage> {
    const accessToken = this.getAccessToken();
    if (!accessToken) return EMPTY;

    return this.http.get<NotificationPage>(
      `${this.apiUrl}/notification/limit/${take}/page/${page}?filter=${filter}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );
  }

  private getAccessToken(): string | null {
    const accessToken = localStorage.getItem('access_token')?.trim();
    return accessToken || null;
  }
}
